import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import {
  containsNoValues,
  checkValidations,
  requiredRule,
  openExternalUrl
} from 'src/libs/qb-brand-web-components'
import { useNavigate } from 'react-router-dom'

import BuyPoints from '../components/buyPoints/BuyPoints'
import {
  DEFAULT_CONTAINER_STATE,
  BUY_POINTS_PROCESS_STEP
} from '../constants/containerStates'
import {
  changeExchangeProvidersState,
  changeExchangeProvidersInputAndErrorState,
  resetExchangeProvider,
  generateBuyPointsCheckoutSession,
  changeBuyPointsState
} from '../store/actions/exchange'
import { EXCHANGE_PROVIDER_PROFILE_DISPATCH } from '../store/reducers/exchange/exchangeProviders'
import { ERROR_TEXT } from '../constants/messages'
import { minPointsRule } from '../util/validation.helpers'
import {
  calculateMinMaxPoints,
  isTokenTypeMiles
} from '../util/pointsExchange.helpers'
import { HOME_URL } from '../constants/navigation'
import { BUY_POINTS_PROFILE_DISPATCH } from '../store/reducers/exchange/buyPoints'
import PurchasedPointsSuccessPopup from '../components/popups/buyPoints/PurchasedPointsSuccess'

const BuyPointsContainer = ({
  changeExchangeProvidersState,
  changeExchangeProvidersInputAndErrorState,
  resetExchangeProvider,
  exchangeProviderProfile,
  tokenProfile,
  generateBuyPointsCheckoutSession,
  buyPointsProfile,
  changeBuyPointsState,
  ...restProps
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [popupState, setPopupState] = useState()
  const [provider, setProvider] = useState(null)

  useEffect(() => {
    handleNoPopup()
    return () => {
      resetExchangeProvider()
    }
  }, [])

  useEffect(() => {
    const checkoutSessionUrl = buyPointsProfile.checkoutSessionUrl
    if (checkoutSessionUrl) {
      openExternalUrl(checkoutSessionUrl, true)
    }
  }, [buyPointsProfile.checkoutSessionUrl])

  useEffect(() => {
    if (buyPointsProfile.paymentSession) {
      setPopupState(BUY_POINTS_PROCESS_STEP.PURCHASED_POINTS_CONFIRMATION)
    }
  }, [buyPointsProfile.paymentSession])

  const handleSelectProvider = (selectedProvider) => {
    setProvider(selectedProvider)
    resetExchangeProvider()
  }

  const handleBackAction = () => {
    setProvider(null)
  }

  const handleSubmitBuyPoints = () => {
    const { pointsAmount } = exchangeProviderProfile
    changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {})
    const pointsErrors = checkValidations({
      pointsAmount: {
        value: pointsAmount,
        rules: [requiredRule]
      }
    })
    if (containsNoValues(pointsErrors)) {
      const tokenSymbol = tokenProfile.symbol
      const { minPoints, maxPoints } = calculateMinMaxPoints(
        provider,
        tokenProfile.onramp
      )
      const validPointsErrorMessage = minPointsRule(
        pointsAmount,
        t,
        minPoints,
        tokenSymbol
      )
      if (validPointsErrorMessage) {
        changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {
          pointsAmount: validPointsErrorMessage
        })
      } else if (pointsAmount > maxPoints) {
        changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {
          pointsAmount: t(ERROR_TEXT.EXCEED_MAX_LIMIT)
        })
      } else {
        if (isTokenTypeMiles(provider?.type)) {
          // Buy points with miles
          // @TODO: Need to integrate MCheckout BE APIs/flow here.
        } else {
          // Buy points with credit card
          generateBuyPointsCheckoutSession(pointsAmount)
        }
      }
    } else {
      changeExchangeProvidersState(
        EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
        pointsErrors
      )
    }
  }

  const handleNoPopup = () => {
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const handlePopupSubmit = () => {
    switch (popupState) {
      case BUY_POINTS_PROCESS_STEP.PURCHASED_POINTS_CONFIRMATION:
        changeBuyPointsState(BUY_POINTS_PROFILE_DISPATCH.PAYMENT_SESSION, null)
        navigate(HOME_URL)
        break

      default:
        setProvider(null)
        handleNoPopup()
        break
    }
  }

  const handlePopupCancel = () => {
    switch (popupState) {
      case BUY_POINTS_PROCESS_STEP.PURCHASED_POINTS_CONFIRMATION:
        handleNoPopup()
        resetExchangeProvider()
        changeBuyPointsState(BUY_POINTS_PROFILE_DISPATCH.PAYMENT_SESSION, null)
        break

      default:
        handleNoPopup()
        break
    }
  }

  const renderPopup = () => {
    switch (popupState) {
      case BUY_POINTS_PROCESS_STEP.PURCHASED_POINTS_CONFIRMATION:
        return (
          <PurchasedPointsSuccessPopup
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            t={t}
            value={buyPointsProfile.paymentSession?.metadata?.pointsAmount}
            {...tokenProfile}
          />
        )

      default:
        break
    }
  }

  return (
    <>
      <BuyPoints
        onSelectProvider={handleSelectProvider}
        onBackAction={handleBackAction}
        selectedProvider={provider}
        t={t}
        onSubmitBuyPoints={handleSubmitBuyPoints}
        tokenProfile={tokenProfile}
        dispatchActionExchange={changeExchangeProvidersInputAndErrorState}
        exchangeProviderProfile={exchangeProviderProfile}
        buyPointsProfile={buyPointsProfile}
        {...restProps}
      />
      {renderPopup()}
    </>
  )
}

const mapStateToProps = ({ mainReducer, exchangeReducer }) => {
  const { tokenProfile } = mainReducer
  const { exchangeProviderProfile, buyPointsProfile } = exchangeReducer
  return {
    tokenProfile,
    exchangeProviderProfile,
    buyPointsProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeExchangeProvidersState,
      changeExchangeProvidersInputAndErrorState,
      resetExchangeProvider,
      generateBuyPointsCheckoutSession,
      changeBuyPointsState
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(BuyPointsContainer)
